import React, { useEffect, useRef, useState } from "react";

/* Scroll reveal: a section lifts 18px and fades in, once, as it enters.

   Three rules keep this on the premium side of the line rather than the
   showreel side:
     1. It happens ONCE. Nothing re-animates on the way back up — re-animation
        is what makes a site feel like a toy.
     2. It is small. 18px and 620ms. If you can describe the movement after
        the fact, it was too big.
     3. It never hides content. The element starts at opacity .001 and is
        revealed by an observer that fires on a 12% intersection; if the
        observer is unavailable, or the visitor has asked for reduced motion,
        the content renders plainly with no animation at all.

   `delay` staggers siblings. Keep it under ~160ms total or the page starts to
   feel like it is loading badly rather than arriving deliberately. */

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type Props = {
  children: React.ReactNode;
  delay?: number;
  as?: "div" | "section";
  className?: string;
  id?: string;
  style?: React.CSSProperties;
};

export default function Reveal({ children, delay = 0, as = "div", className, id, style }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Static when motion is unwanted or unsupported: render shown, never animate.
  const [shown, setShown] = useState(() => prefersReducedMotion() || typeof IntersectionObserver === "undefined");

  useEffect(() => {
    if (shown) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect(); // once only
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown]);

  const Tag = as as any;
  return (
    <Tag
      ref={ref}
      id={id}
      className={className}
      style={{
        ...style,
        opacity: shown ? 1 : 0.001,
        transform: shown ? "none" : "translateY(18px)",
        transition: `opacity 620ms cubic-bezier(.22,.61,.36,1) ${delay}ms, transform 620ms cubic-bezier(.22,.61,.36,1) ${delay}ms`,
        willChange: shown ? "auto" : "opacity, transform",
      }}
    >
      {children}
    </Tag>
  );
}
